import { useEffect, useState } from "react"
import { IFirebasexMessaging } from "cordova-plugin-firebasex-messaging"
import { UnreachableCaseError } from "shared/Helpers/lang"
import { Logger } from "shared/Helpers/logging"
import { CordovaStatus } from "../cordovaTypes"
import { useCordova } from "./useCordova"

/**
 * Push notifications always go through cordova-plugin-firebasex-messaging (window.FirebasexMessaging).
 * The plugin is only present in native app builds that are new enough; in the browser and in older
 * app versions it is missing and push notifications are disabled.
 */
export interface FirebaseMessaging {
    getToken(): Promise<string>
    onTokenRefresh(onToken: (token: string) => void, onError: (error: string) => void): void
    requestPermission(): Promise<void>
    hasPermission(): Promise<boolean>
    /** iOS: the user tapped the app's button in the iOS notification settings (see requestPermission) */
    onOpenSettings(onOpen: () => void, onError: (error: string) => void): void
    onMessage(onMessage: (payload: Record<string, any>) => void, onError: (error: string) => void): void
}

class FirebasexMessagingProxy implements FirebaseMessaging {
    private readonly messaging: IFirebasexMessaging

    constructor(messaging: IFirebasexMessaging) {
        this.messaging = messaging
    }

    requestPermission(): Promise<void> {
        const messaging = this.messaging
        return new Promise((resolve, reject) => {
            // true: iOS 12+ shows an in-app settings button in the iOS notification settings [see onOpenSettings]
            messaging.grantPermission((granted) => granted === false ? reject("User denied permission") : resolve(), (error) => reject(error), true)
        })
    }

    hasPermission(): Promise<boolean> {
        const messaging = this.messaging
        const hasPermission = messaging.hasPermission
        if (typeof hasPermission !== "function") return Promise.reject("hasPermission not supported by plugin version")
        return new Promise((resolve, reject) => {
            hasPermission.call(messaging, (granted: boolean) => resolve(granted), (error: string) => reject(error))
        })
    }

    onOpenSettings(onOpen: () => void, onError: (error: string) => void) {
        // iOS only: the method is not present on Android
        const onOpenSettings = this.messaging.onOpenSettings
        if (typeof onOpenSettings !== "function") return
        onOpenSettings.call(this.messaging, onOpen, onError)
    }

    getToken(): Promise<string> {
        const messaging = this.messaging
        return new Promise((resolve, reject) => {
            messaging.getToken((token) => resolve(token), (error) => reject(error))
        })
    }

    onTokenRefresh(refreshHandler: (token: string) => void, errorHandler: (error: string) => void) {
        this.messaging.onTokenRefresh(refreshHandler, errorHandler)
    }

    onMessage(messageHandler: (payload: Record<string, any>) => void, errorHandler: (error: string) => void) {
        this.messaging.onMessageReceived(messageHandler, errorHandler)
    }
}

export enum FirebaseStatus {
    // Cordova has not settled yet, so we do not know whether the plugin is there
    Initializing = "Initializing",
    // Not in the app, or an app version without the plugin [push notifications disabled]
    Unavailable = "Unavailable",
    Ready = "Ready",
}

export type FirebaseState =
    | Readonly<{ status: FirebaseStatus.Initializing }>
    | Readonly<{ status: FirebaseStatus.Unavailable, reason: string }>
    | Readonly<{ status: FirebaseStatus.Ready, messaging: FirebaseMessaging }>

export function useFirebase(): FirebaseState {
    const cordova = useCordova()
    const [state, setState] = useState<FirebaseState>({ status: FirebaseStatus.Initializing })
    const logger = new Logger("firebase")

    useEffect(() => {
        switch (cordova.status) {
            case CordovaStatus.Initializing:
                // Ignore
                break
            case CordovaStatus.DeviceReady:
                logger.info("Received Cordova deviceready event")
                if (typeof window.FirebasexMessaging === "object" && window.FirebasexMessaging !== null) {
                    logger.info("Found cordova-plugin-firebasex-messaging [push notifications enabled]")
                    setState({ status: FirebaseStatus.Ready, messaging: new FirebasexMessagingProxy(window.FirebasexMessaging) })
                } else {
                    logger.warn("cordova-plugin-firebasex-messaging not available, app version too old? [push notifications disabled]")
                    setState({ status: FirebaseStatus.Unavailable, reason: "Firebase messaging plugin missing" })
                }
                break
            case CordovaStatus.Unavailable:
                logger.info("Cordova not available [push notifications disabled]")
                setState({ status: FirebaseStatus.Unavailable, reason: "Cordova not available" })
                break
            default:
                throw new UnreachableCaseError(cordova)
        }
    }, [cordova.status])

    return state
}

/** The Firebase messaging wrapper when the plugin is ready, otherwise undefined */
export function useFirebaseMessaging(): FirebaseMessaging | undefined {
    const state = useFirebase()
    return state.status === FirebaseStatus.Ready ? state.messaging : undefined
}
